const liquidTemplateTokenizer = (liquidTemplate: string) => {
  // strip template tags from template and replace with a placeholder
  return liquidTemplate
    .replace(/{{.*?}}/g, '%diff-split%')
    .replace(/{%.*?%}/g, '%diff-split%')
    .split('%diff-split%');
};

const hasLiquidIteration = (template: string): boolean => {
  return /{%\s*(for|tablerow|paginate)\s+\w+\s+in\s+\w+\s*%}/.test(template);
};

const hasLiquidControlFlow = (template: string): boolean => {
  return /{%\s*(if|elsif|else|unless|case|when)\s+\w+\s*%}/.test(template);
};

/**
 * Return true if template contains anything more advanced than simple string
 * interpolation
 */
const hasAdvancedLiquid = (template: string): boolean => {
  return hasLiquidIteration(template) || hasLiquidControlFlow(template);
};

/**
 * Compares a template with its rendered version and highlights the differences.
 *
 * @param originalTemplate - The original template.
 * @param renderedTemplate - The rendered version of the template.
 * @param highlightClassname - The CSS class name to apply for highlighting the
 *   differences.
 * @returns The rendered template with differences highlighted.
 */
export const templateDiff = (
  originalTemplate: string,
  renderedTemplate: string,
  highlightClassname = 'highlighted',
  tokenizer = liquidTemplateTokenizer,
): string => {
  if (hasAdvancedLiquid(originalTemplate)) {
    // Advanced Liquid templates are not supported
    return renderedTemplate;
  }

  const templateTokens = tokenizer(originalTemplate);

  // highlight differences
  let indexFloor = 0;
  let mismatch = false;
  const highlighted = templateTokens.reduce((pre, current, index) => {
    const currentToken = current;
    if (index === templateTokens.length - 1) return pre + currentToken;

    const nextToken = templateTokens[index + 1];

    // since the tokens could be repeated in the template, we need to make sure we are
    // always looking ahead from the last index we found a token at
    const renderedTemplateAfterPrevious =
      renderedTemplate.substring(indexFloor);
    const tokenIndex =
      renderedTemplateAfterPrevious.indexOf(currentToken) + currentToken.length;
    const renderedTemplateAfterToken =
      renderedTemplateAfterPrevious.substring(tokenIndex);
    const nextTokenIndex = renderedTemplateAfterToken.indexOf(nextToken);

    if (tokenIndex === -1 || nextTokenIndex === -1) {
      mismatch = true;
      return pre + currentToken;
    }

    // find value between tokens in rendered template
    const renderedValue = renderedTemplateAfterPrevious.substring(
      tokenIndex,
      tokenIndex + nextTokenIndex,
    );
    indexFloor += tokenIndex + nextTokenIndex;

    return (
      pre +
      currentToken +
      `<span class="${highlightClassname}">${renderedValue}</span>`
    );
  }, '');

  if (mismatch) {
    return renderedTemplate;
  }

  return highlighted;
};
